import { filterHiddenExtendedButton } from "./controller/MenuItemController";
import { Constants, FOCUS_ON_OPEN, FOCUS_SETTINGS, NEW_PAGE_MINIMIZED, TOGGLE_SOUND } from "./model/Constants";

/**
 * @class {Properties}
 * Properties class initializes the ui and state properties either from the properties passed from the bootstrap or by setting the default values
 */

class Properties {
    typingMsg: any;

    stoppedTypingMsg: any;

    openerText: any;

    serviceNotAvailable: any;

    networkDisconnected: any;

    networkConnected: any;

    networkFailed: any;

    customerName: any;

    sdkInst: any;

    agentAlias: any;

    serviceQueued: any;

    agentAvailable: any;

    agentExitText: any;

    agentResolvedText: any;

    agentInitiateTransfer: any;

    agentLostText: any;

    agentTransferLostText: any;

    agentTransferStatusText: any;

    ninaCoachStatusText: any;

    conferenceModeText: any;

    closeConfirmationText: any;

    closeModalNegativeButtonText: any;

    closeModalPositiveButtonText: any;

    historyLoadingText: any;

    thankyouImageLabel: any;

    emailModalInputLabel: any;

    emailModalOkButtonText: any;

    emailModalCancelButtonText: any;

    emailModalSendButtonText: any;

    emailModalMailFailText: any;

    emailModalMailSentText: any;

    emailModalMailValidText: any;

    disclaimerText: any;

    autoEngageText: any;

    audioTextOn: any;

    audioTextOff: any;

    ariaStrings: { opensNewWindow: string; cardDeckReader: string; quickReplyReader: string; transcriptLabel: string; chatWindowTitle: string };

    queuedMsgInAsync: any;

    persistQueueMessages: any;

    showConvHeader: any;

    showStoppedTyping: any;

    showTransferInitiateMessage: any;

    showTransferStatusMessage: any;

    showNinaCoachStatusMessage: any;

    showConferenceModeMessage: any;

    enableMsgGrouping: any;

    showTransferLostMessage: any;

    showMemberLostMessage: any;

    openerDelay: any;

    showAsyncChatExitMsg: any;

    showAsyncChatResolvedMsg: any;

    showDisclaimerText: any;

    vaTyping: any;

    startAutoEngagement: any;

    waitForOpener: any;

    queueAsAgent: any;

    audioInitialSetting: any;

    networkMessageDelay: any;

    focusOnInteractiveElement: any;

    focusOnAgentMessage: any;

    focusOnOpen: any;

    focusInputOnVADialogClick: any;

    minimizeOnNewPage: any;

    minimizeAndCloseAccessKeys: any;

    reverseCloseModalButtons: any;

    reverseEmailModalButtons: any;

    extendedSurveyTopBarVisible: boolean;

    extendedSurveyFooterVisible: boolean;

    moveEmailCancelBtnToBottom: any;

    emailModalAutocomplete: boolean;

    /**
     * initializes the Properties instance variables with values from UIConfig#stringProps Json Object
     * @param {Object} stringProps
     */
    initAppStringProperties(stringProps: any = {}) {
        this.typingMsg = stringProps.startTyping || "Agent is typing";
        this.stoppedTypingMsg = stringProps.stoppedTyping || "Agent stopped typing";
        this.openerText = stringProps.openerText;
        this.serviceNotAvailable = typeof stringProps.messagingNotAvailable === "undefined" ? "Messaging service not available at this time. Please try again later." : stringProps.messagingNotAvailable;
        this.networkDisconnected = stringProps.messagingDisconnected || "Connection to messaging server is disconnected. Please check your network.";
        this.networkConnected = stringProps.messagingReconnected || "Connection to messaging server is restored.";
        this.networkFailed = stringProps.messagingFailed || "Unable to establish a connection to server. please try again later.";
        this.customerName = stringProps.customerName || this.sdkInst.getCustomerName();
        this.agentAlias = stringProps.agentAlias;
        this.serviceQueued = typeof stringProps.serviceQueued === "undefined" ? "An agent will respond as soon as they are available" : stringProps.serviceQueued;
        this.agentAvailable = typeof stringProps.agentAvailable === "undefined" ? "Agent now available" : stringProps.agentAvailable;
        this.agentExitText = stringProps.agentExitText;
        this.agentResolvedText = stringProps.agentResolvedText;
        this.agentInitiateTransfer = stringProps.agentInitiateTransfer;
        this.agentLostText = stringProps.agentLostText;
        this.agentTransferLostText = stringProps.agentTransferLostText;
        this.agentTransferStatusText = stringProps.agentTransferStatusText;
        this.ninaCoachStatusText = stringProps.ninaCoachStatusText;
        this.conferenceModeText = stringProps.conferenceModeText;
        this.closeConfirmationText = stringProps.closeConfirmationText || "Are you sure you want to end the chat?";
        this.closeModalNegativeButtonText = stringProps.closeModalNegativeButtonText || Constants.CANCEL;
        this.closeModalPositiveButtonText = stringProps.closeModalPositiveButtonText || "End Chat";
        this.historyLoadingText = stringProps.historyLoadingText || "We are loading recent chat history, please hold...";
        this.thankyouImageLabel = stringProps.thank_you_image_label;

        // Email modal specific strings (so clients can override)
        this.emailModalInputLabel = stringProps.emailModalInputLabel || "Enter your email address.";
        this.emailModalOkButtonText = stringProps.emailModalOkButtonText || Constants.OK;
        this.emailModalCancelButtonText = stringProps.emailModalCancelButtonText || Constants.CANCEL;
        this.emailModalSendButtonText = stringProps.emailModalSendButtonText || "Send";
        this.emailModalMailFailText = stringProps.emailModalMailFailText || "Invalid mail address. Please enter a valid email address.";
        this.emailModalMailSentText = stringProps.emailModalMailSentText || "Your transcript has been sent to your mail.";
        this.emailModalMailValidText = stringProps.emailModalMailValidText || "Valid mail address.";

        this.disclaimerText = stringProps.disclaimerHeaderText;
        this.autoEngageText = stringProps.autoEngageText;
        this.audioTextOn = stringProps.audioTextOn;
        this.audioTextOff = stringProps.audioTextOff;

        // ARIA strings
        this.ariaStrings = {
            opensNewWindow: "Opens in a new window",
            cardDeckReader: "Card, 1 of {total}. Navigate to other entries with buttons in transcript.",
            quickReplyReader: "Interactive buttons, group of {total}.",
            transcriptLabel: "Chat transcript",
            chatWindowTitle: "Chat Window",
        };

        // If ariaStrings in stringProps override current defaults.
        for (const prop in stringProps.ariaStrings) {
            this.ariaStrings[prop] = stringProps.ariaStrings[prop];
        }
    }

    /**
     * initializes the Properties instance variables with values from UIConfig#configProps Json Object
     * @param {Object} configProps
     */

    initConfigProperties(configProps: any = {}) {
        this.queuedMsgInAsync = typeof configProps.displayQueuedMessagesInAsync === "undefined" ? false : configProps.displayQueuedMessagesInAsync;
        this.persistQueueMessages = configProps.persistQueueMessages;
        this.showConvHeader = typeof configProps.showConversationHeader === "undefined" ? true : configProps.showConversationHeader;
        this.showStoppedTyping = typeof configProps.shouldDisplayStoppedTyping === "undefined" ? true : configProps.shouldDisplayStoppedTyping;
        this.emailModalAutocomplete = typeof configProps.emailModalAutocomplete === "undefined" ? true : configProps.emailModalAutocomplete;
        /**
         * Control the display of transfer initate message
         * Customer will be aware of the transfer happening either from a VA to Live agent or live agent to another live agent
         */
        this.showTransferInitiateMessage = typeof configProps.showTransferInitiateMessage === "undefined" ? true : configProps.showTransferInitiateMessage;

        /**
         * Control the display of transfer status message
         * Customer will know that he can start asking the questions
         */
        this.showTransferStatusMessage = typeof configProps.showTransferStatusMessage === "undefined" ? true : configProps.showTransferStatusMessage;

        /**
         * Should CI display a system message when Nina Coach takes over.
         */
        this.showNinaCoachStatusMessage = typeof configProps.showNinaCoachStatusMessage === "undefined" ? true : configProps.showNinaCoachStatusMessage;

        /**
         * Should CI display when Chat is in conference mode.
         * Customer will be made aware of another person joined in the chat
         */
        this.showConferenceModeMessage = typeof configProps.showConferenceModeMessage === "undefined" ? true : configProps.showConferenceModeMessage;

        /**
         * Should CI group agent or customer message bubbles that are adjucent.
         * @type {Object.enableMsgGrouping}
         */
        this.enableMsgGrouping = configProps.enableMsgGrouping;

        /**
         * Should ci display a system message for the agent that is left during a transfer.
         */
        this.showTransferLostMessage = configProps.showTransferLostMessage;

        /**
         * Should ci display a system message when an agent is lost from chat room.
         */
        this.showMemberLostMessage = typeof configProps.showMemberLostMessage === "undefined" ? true : configProps.showMemberLostMessage;

        /**
         * Delay that is injected after the display of first opener and the openers that follows
         * @type {number}
         */
        this.openerDelay = configProps.openerDisplayInterval || 3000;

        /**
         * Configure the display of async chat exit system message.
         * Usually async chats can be continued even if agent exit, so might not make sense to display by default
         */
        this.showAsyncChatExitMsg = typeof configProps.showAsyncChatExitMsg === "undefined" ? false : configProps.showAsyncChatExitMsg;

        /**
         * Configure the display of async chat resolved system message.
         */
        this.showAsyncChatResolvedMsg = typeof configProps.showAsyncChatResolvedMsg === "undefined" ? false : configProps.showAsyncChatResolvedMsg;

        /**
         * Configure the display of disclaimer text in the header of transcript.
         * @type {boolean}
         */
        this.showDisclaimerText = typeof configProps.showHeaderDisclaimer === "undefined" ? false : configProps.showHeaderDisclaimer;

        /**
         * Allowing CI to display a type indicator and the delay that is need before displaying it.
         * This way if VA response is delayed then a typing indicator is displayed to user
         * @type {number}
         */
        this.vaTyping = configProps.vaTypingIndiatorDelay || 0;

        /**
         * Allowing CI to start an engagement automatically after CI is displayed
         * @type {Boolean}
         */
        this.startAutoEngagement = configProps.startAutoEngagement;

        /**
         * Configure CI to enable input only after opener is fetched and displayed, by default it is false.
         * This way customer get a chance to see the personalized welcome message from VA when auto engagement is enabled
         */
        this.waitForOpener = configProps.disableFooterUntilOpener;

        /**
         * Allowing queue messages to display as agent message, by default it is false and display as system message
         */
        this.queueAsAgent = configProps.showQueueMsgAsAgent;
        this.audioInitialSetting = configProps.audioInitialSetting || TOGGLE_SOUND.SOUND_ON;

        /**
         * Inject a small delay before displaying the network disconnect message.
         * This way network disconnect message won't appear if disconnect and reconnect happens very fast
         * @type {number}
         */
        this.networkMessageDelay = configProps.networkMessageDelay || 3000;

        /**
         * CI set focus to first links found in agent/VA message, optionally this can be turned off
         * @type {boolean}
         * @deprecated
         */
        this.focusOnInteractiveElement = typeof configProps.focusOnInteractiveElement === "undefined" ? true : configProps.focusOnInteractiveElement;

        /**
         * Config to control where the focus goes when a new message comes
         * @type {FOCUS_SETTINGS}
         */
        this.focusOnAgentMessage = configProps.focusOnAgentMessage || FOCUS_SETTINGS.ON_INPUT;

        this.focusOnOpen = configProps.focusOnOpen || FOCUS_ON_OPEN.ON_INPUT;

        /**
         * configuration allowing CI to set focus to Text Input after user clicks a virtual agent dialog link
         * @type {Boolean| undefined}
         */
        this.focusInputOnVADialogClick = configProps.focusInputOnVADialogClick;

        /**
         * Configuration allowing CI to be minimized after page refresh,
         * Depends upon the device chat window will appear minimze
         * after user navigated to another or same page
         * @type {String}
         */
        this.minimizeOnNewPage = configProps.minimizeOnNewPage || NEW_PAGE_MINIMIZED.PHONE;

        /**
         * Configuration allowing CI to be minimized or closed based on certain key combinations
         * default key combination is Alt + 2 for minimizing and Alt + 9 for closing
         * closeKey and minimizeKey stores the corresponding keyCode for digit between 0 to 9
         * @param {Object}
         */
        this.minimizeAndCloseAccessKeys = configProps.minimizeAndCloseAccessKeys || {
            mainKey: "altKey",
            closeKey: 57,
            minimizeKey: 50,
        };

        this.reverseCloseModalButtons = configProps.reverseCloseModalButtons;
        this.reverseEmailModalButtons = configProps.reverseEmailModalButtons;
        this.moveEmailCancelBtnToBottom = typeof configProps.moveEmailCancelBtnToBottom === "undefined" ? true : configProps.moveEmailCancelBtnToBottom;
    }

    /**
     * filter the hidden menu items based on the device type
     * @param {Object} config
     */
    filterHiddenMenu(config) {
        const topBarSettings = config.topBarConfig.topBarSettings;

        if (topBarSettings && topBarSettings.extendedTopBarItems) {
            topBarSettings.extendedTopBarItems = filterHiddenExtendedButton(
                topBarSettings.extendedTopBarItems, this.sdkInst.getDeviceType()
            );

            if (topBarSettings.extendedTopBarItems.length === 0) {
                topBarSettings.extendedTopBarItems = null;
            }
        }

        const footerSettings = config.footerConfig.footerSettings;
        if (footerSettings && footerSettings.extendedFooterItems) {
            footerSettings.extendedFooterItems = filterHiddenExtendedButton(
                footerSettings.extendedFooterItems, this.sdkInst.getDeviceType()
            );
            if (footerSettings.extendedFooterItems.length === 0) {
                footerSettings.extendedFooterItems = null;
            }
        }

        if (footerSettings && footerSettings.leftFooterItems) {
            footerSettings.leftFooterItems = filterHiddenExtendedButton(
                footerSettings.leftFooterItems, this.sdkInst.getDeviceType()
            );
            if (footerSettings.leftFooterItems.length === 0) {
                footerSettings.leftFooterItems = null;
            }
        }

        if (footerSettings && footerSettings.rightFooterItems) {
            footerSettings.rightFooterItems = filterHiddenExtendedButton(
                footerSettings.rightFooterItems, this.sdkInst.getDeviceType()
            );
            if (footerSettings.rightFooterItems.length === 0) {
                footerSettings.rightFooterItems = null;
            }
        }
    }

    /**
     * filter the survey extended topbar items and footer items based on the survey config key
     * @param {Object} topBarSettings
     * @param {Object} footerSettings
     */
    filterSurveyItem(topBarSettings, footerSettings) {
        if (topBarSettings) {
            this.extendedSurveyTopBarVisible = !!(topBarSettings.extendedTopBarInSurvey && topBarSettings.extendedTopBarItems);
        }

        if (footerSettings) {
            this.extendedSurveyFooterVisible = !!(footerSettings.extendedFooterInSurvey && footerSettings.extendedFooterItems);
        }
    }
}

export default Properties;
